import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, Calendar, MapPin, Download, CheckCircle, AlertCircle, AlertTriangle, FileText, Link as LinkIcon, ZoomIn, ZoomOut, Move, MessageSquare, Send } from 'lucide-react';
import { doc, getDoc, updateDoc, increment, arrayUnion, collection, addDoc, query, where, orderBy, onSnapshot, deleteDoc, runTransaction } from 'firebase/firestore';
import { sweepDb, portalDb, portalAuth } from '../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { toast } from 'react-hot-toast';
import { PDFDocument, rgb } from 'pdf-lib';

const ReportModal = ({ reportId, onClose, userData, currentUser }) => {
  const [user] = useAuthState(portalAuth);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  
  // Add touch-related state
  const [touchDistance, setTouchDistance] = useState(null);
  
  // Add messages-related state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Add PDF-related state
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  // Effect to ensure we redirect from photos tab if no photos are available
  useEffect(() => {
    if (report && activeTab === 'photos') {
      if (!report.imageUrls || !Array.isArray(report.imageUrls) || report.imageUrls.length === 0) {
        setActiveTab('details');
      }
    }
  }, [report, activeTab]);
  
  // Simple fetch without view tracking
  useEffect(() => {
    let isMounted = true;
    
    const fetchReport = async () => {
      setIsLoading(true);
      
      try {
        const reportRef = doc(sweepDb, 'inspections', reportId);
        const reportDoc = await getDoc(reportRef);
        
        if (!isMounted) return;
        
        if (reportDoc.exists()) {
          setReport(reportDoc.data());
        } else {
          toast.error('Report not found');
          if (isMounted) onClose();
        }
      } catch (error) {
        console.error('Error fetching report:', error);
        toast.error('Error loading report');
        if (isMounted) onClose();
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchReport();
    
    // Ensure modal can be closed with ESC key
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      isMounted = false;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [reportId, onClose]);

  // Separate effect for view tracking that runs only once
  useEffect(() => {
    // Only run view tracking if we have a reportId
    if (!reportId) return;
    
    const trackView = async () => {
      try {
        // Simple way to increment the view count
        const reportRef = doc(sweepDb, 'inspections', reportId);
        
        // Get user info if available
        let userName = 'Portal User';
        let userEmail = 'Unknown';
        
        if (user?.uid) {
          try {
            const userDoc = await getDoc(doc(portalDb, 'users', user.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.firstName && userData.lastName) {
                userName = `${userData.firstName} ${userData.lastName}`;
              } else if (userData.companyName) {
                userName = userData.companyName;
              }
              userEmail = userData.email || user.email || 'Unknown';
            }
          } catch (error) {
            console.error('Error getting user data:', error);
          }
        }
        
        // Create new view log entry
        const newViewEntry = {
          timestamp: new Date().toISOString(),
          userId: user?.uid || 'portal-user',
          userName: userName,
          userEmail: userEmail,
          source: 'oiltankportal',
          viewType: 'portal-dashboard-view'
        };
        
        try {
          // Use arrayUnion to safely add the entry to the array
          // This works even if viewLog doesn't exist yet or isn't an array
          await updateDoc(reportRef, {
            viewCount: increment(1),
            viewLog: arrayUnion(newViewEntry)
          });
          
          console.log('View tracked successfully in OilTankSweep database');
        } catch (error) {
          console.error('Error updating viewLog:', error);
          
          // Fallback: If arrayUnion fails, just set it directly
          try {
            await updateDoc(reportRef, {
              viewCount: increment(1)
            });
            console.log('View count updated (but viewLog failed)');
          } catch (fallbackError) {
            console.error('Complete view tracking failure:', fallbackError);
          }
        }
      } catch (error) {
        console.warn('View tracking failed:', error);
      }
    };
    
    // Call the tracking function
    trackView();
    
    // Empty dependency array ensures this runs exactly once when component mounts
  }, []);

  // Fetch messages for this report
  useEffect(() => {
    if (!reportId || !user) return;
    
    setIsLoadingMessages(true);
    
    // Create a query against the messages collection
    const messagesRef = collection(portalDb, 'reportMessages');
    const q = query(
      messagesRef,
      where('reportId', '==', reportId),
      orderBy('timestamp', 'asc')
    );
    
    // Listen for real-time updates
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const messagesList = [];
      querySnapshot.forEach((doc) => {
        messagesList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setMessages(messagesList);
      setIsLoadingMessages(false);
      
      // Mark messages as read if actively viewing messages tab
      if (activeTab === 'messages') {
        markMessagesAsRead(messagesList);
      }
      
      // Scroll to bottom when new messages arrive
      setTimeout(() => {
        if (messagesEndRef.current && activeTab === 'messages') {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    });
    
    // Clean up listener
    return () => unsubscribe();
  }, [reportId, user, activeTab]);
  
  // Mark inspector messages as read when client views them
  const markMessagesAsRead = async (messagesList) => {
    // Filter for unread messages not sent by the current user
    const unreadMessages = messagesList.filter(msg => 
      msg.senderId !== user.uid && !msg.read
    );
    
    // If there are unread messages, mark them as read
    if (unreadMessages.length > 0) {
      try {
        // Process each message update individually
        const updatePromises = unreadMessages.map(msg => {
          const messageRef = doc(portalDb, 'reportMessages', msg.id);
          return updateDoc(messageRef, { read: true });
        });
        
        // Wait for all updates to complete
        await Promise.all(updatePromises);
        
        // Update the local state immediately to remove the badge
        // This ensures UI updates even if the Firestore listener hasn't triggered yet
        setMessages(prev => 
          prev.map(msg => 
            msg.senderId !== user.uid && !msg.read 
              ? {...msg, read: true} 
              : msg
          )
        );
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    }
  };

  // Format address by removing USA
  const formatAddress = (address) => {
    if (!address) return 'Address not available';
    return address
      .replace(/,\s*USA$/i, '')
      .replace(/\s*USA$/i, '');
  };

  // Get scenario icon and information
  const getScenarioInfo = (scenario) => {
    switch(scenario) {
      case 'noTank':
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          label: 'No underground storage tank detected',
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'anomaly':
        return {
          icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
          label: 'Metallic anomaly detected',
          color: 'text-amber-500',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200'
        };
      case 'ustFound':
      case 'tankFound':
        return {
          icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
          label: 'Underground storage tank identified',
          color: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200'
        };
      default:
        return {
          icon: <AlertCircle className="w-5 h-5 text-gray-500" />,
          label: 'Status unknown',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Restore the original renderReportContent function and add resources section
  const renderReportContent = () => {
    if (!report) return null;

    // Add Resources Section
    const resourcesSection = report.resources && report.resources.length > 0 ? (
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Additional Resources
        </h4>
        <ul className="space-y-2">
          {report.resources.map((resource, index) => (
            <li 
              key={index} 
              className="flex items-center p-2 bg-white rounded border border-gray-200"
            >
              {resource.type === 'file' ? (
                <FileText className="h-4 w-4 text-blue-500 mr-3 flex-shrink-0" />
              ) : (
                <LinkIcon className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
              )}
              <div className="flex-grow overflow-hidden">
                <p className="text-sm font-medium text-gray-800 truncate">{resource.title}</p>
              </div>
              <a 
                href={resource.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-3 px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors duration-150 flex items-center"
              >
                View <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

    // If the report has fullDocumentText, we'll use that directly
    if (report.fullDocumentText) {
      // Split by paragraphs and render with spacing
      return (
        <>
          {/* Insert Resources Section at the top */}
          {resourcesSection}
          
          {/* Original report content */}
          {report.fullDocumentText.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="mb-4">{paragraph}</p>
          ))}
        </>
      );
    }

    // Otherwise, we'll build it from the individual fields
    let content = [];
    
    // Insert Resources Section at the top
    if (resourcesSection) {
      content.push(resourcesSection);
    }

    // Introduction with the appropriate conclusion based on scenario
    let introText = "Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). ";
    
    if (report.scenario === 'noTank') {
      introText += "We did not detect any anomalies with our metal detection equipment that match the size of a UST.";
      
      // Add limitations text 
      if (report.limitations?.length > 0) {
        introText += " Our inspection was limited by " + 
          report.limitations.map(limitation => {
            const type = limitation.type === 'custom' ? limitation.customType : limitation.type;
            const location = limitation.location === 'custom' ? limitation.customLocation : limitation.location;
            return `${type} ${location}`;
          }).join(", ") + ".";
      }
      
      // Add custom text if available
      if (report.customText) {
        introText += ` ${report.customText}`;
      }
      
      introText += " This does not guarantee the absence of USTs on the property; but it is unlikely.";
    } 
    else if (report.scenario === 'anomaly') {
      introText += `A metallic anomaly, similar in size to an underground storage tank (UST), was detected${
        report.anomalyLocation ? " " + (report.anomalyLocation === 'custom' ? report.customLocation : report.anomalyLocation) : ""
      }.`;
      
      // Add limitations text
      if (report.limitations?.length > 0) {
        introText += " Our inspection was limited by " + 
          report.limitations.map(limitation => {
            const type = limitation.type === 'custom' ? limitation.customType : limitation.type;
            const location = limitation.location === 'custom' ? limitation.customLocation : limitation.location;
            return `${type} ${location}`;
          }).join(", ") + ".";
      }
      
      // Add custom text if available
      if (report.customText) {
        introText += ` ${report.customText}`;
      }
      
      introText += " We recommend requesting OPRA records for any information on previously decommissioned oil tanks. If no records are found, proceed with an intrusive investigation.";
    } 
    else if (report.scenario === 'ustFound' || report.scenario === 'tankFound') {
      introText += `An underground storage tank (UST) was identified${
        report.anomalyLocation ? " " + (report.anomalyLocation === 'custom' ? report.customLocation : report.anomalyLocation) : ""
      }.`;
      
      // Add limitations text
      if (report.limitations?.length > 0) {
        introText += " Our inspection was limited by " + 
          report.limitations.map(limitation => {
            const type = limitation.type === 'custom' ? limitation.customType : limitation.type;
            const location = limitation.location === 'custom' ? limitation.customLocation : limitation.location;
            return `${type} ${location}`;
          }).join(", ") + ".";
      }
      
      // Add custom text if available
      if (report.customText) {
        introText += ` ${report.customText}`;
      }
      
      introText += " This UST may pose a risk of contamination if not addressed. We strongly recommend the removal or proper decommissioning of this UST, along with soil testing for potential contamination.";
    }
    
    content.push(<p key="intro" className="mb-4">{introText}</p>);

    // Standard equipment and methodology text
    content.push(
      <p key="equipment" className="mb-4">
        Utilizing electronic metal detection equipment (Fisher TW-6), and a magnetic locating device (Schonstedt 92XTD), 
        the grounds were swept for any buried metallic objects representing a UST. The detection equipment used can detect
        metallic objects up to a depth of six feet. The property was swept to a distance of at least 30ft around the 
        perimeter of the house.
      </p>
    );

    content.push(
      <p key="conclusion2" className="mb-4">
        No other metal anomalies consistent with the size of an underground storage tank were identified during the 
        oil tank sweep.
      </p>
    );

    content.push(
      <p key="contact" className="mb-4">
        If you have any questions about this report please feel free to contact us.
      </p>
    );

    content.push(
      <p key="terms" className="mb-4 text-sm text-gray-600">
        Terms and Conditions: This report excludes areas where access to the magnetic locator was restricted due to 
        obstructions such as dense vegetation, denied access, or structural interference. The methods used at this 
        site have been used successfully at many sites to locate underground storage tanks and drums. There can be 
        no guarantee, however, that every target will be detected at a particular site. Subsurface conditions (e.g. 
        rebar/wire mesh under concrete, foundations, structures and others) may prevent some or all geophysical 
        locating methods from working properly.
      </p>
    );

    return content;
  };

  // Add handlers for image zoom and pan
  const handleImageClick = () => {
    setShowImageZoom(true);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
  };

  const handleCloseZoom = () => {
    setShowImageZoom(false);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ 
        x: e.clientX - panPosition.x, 
        y: e.clientY - panPosition.y 
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add touch handlers for pinch zoom
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Calculate initial distance between two touch points for pinch-to-zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      setTouchDistance(distance);
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Single touch for panning
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - panPosition.x,
        y: e.touches[0].clientY - panPosition.y
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent default to avoid page scrolling

    if (e.touches.length === 2 && touchDistance !== null) {
      // Handle pinch zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDistance = Math.sqrt(dx * dx + dy * dy);
      
      if (newDistance > touchDistance) {
        // Pinch out - zoom in
        setZoomLevel(prev => Math.min(prev + 0.05, 5));
      } else {
        // Pinch in - zoom out
        setZoomLevel(prev => Math.max(prev - 0.05, 1));
      }
      
      setTouchDistance(newDistance);
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      // Handle panning with single touch
      setPanPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTouchDistance(null);
  };

  // Add keyboard handler for the zoom view
  const handleKeyDown = (e) => {
    if (showImageZoom) {
      switch (e.key) {
        case 'Escape':
          handleCloseZoom();
          break;
        case 'ArrowUp':
        case 'w':
          if (zoomLevel > 1) {
            setPanPosition(prev => ({ ...prev, y: prev.y + 20 }));
          }
          break;
        case 'ArrowDown':
        case 's':
          if (zoomLevel > 1) {
            setPanPosition(prev => ({ ...prev, y: prev.y - 20 }));
          }
          break;
        case 'ArrowLeft':
        case 'a':
          if (zoomLevel > 1) {
            setPanPosition(prev => ({ ...prev, x: prev.x + 20 }));
          } else if (activeImageIndex > 0) {
            setActiveImageIndex(prev => prev - 1);
          }
          break;
        case 'ArrowRight':
        case 'd':
          if (zoomLevel > 1) {
            setPanPosition(prev => ({ ...prev, x: prev.x - 20 }));
          } else if (report?.imageUrls && activeImageIndex < report.imageUrls.length - 1) {
            setActiveImageIndex(prev => prev + 1);
          }
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
        case '_':
          handleZoomOut();
          break;
        case '0':
          setZoomLevel(1);
          setPanPosition({ x: 0, y: 0 });
          break;
        default:
          break;
      }
    }
  };

  // Handle double click to zoom
  const handleDoubleClick = (e) => {
    if (zoomLevel === 1) {
      setZoomLevel(2.5);
      // Center zoom on click position
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left - rect.width / 2;
      const offsetY = e.clientY - rect.top - rect.height / 2;
      setPanPosition({ x: -offsetX, y: -offsetY });
    } else {
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
    }
  };

  // Update effect to include keyboard events
  useEffect(() => {
    if (showImageZoom) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchend', handleTouchEnd);
      window.addEventListener('touchcancel', handleTouchEnd);
      window.addEventListener('keydown', handleKeyDown);
      
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('touchcancel', handleTouchEnd);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showImageZoom, isDragging, dragStart, activeImageIndex, zoomLevel]);

  // Add function to delete a message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      // Reference to the message document
      const messageRef = doc(portalDb, 'reportMessages', messageId);
      
      // Delete the message
      await deleteDoc(messageRef);
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  // Update the getUserDisplayName function to use userData when available
  const getUserDisplayName = (user) => {
    // If we have userData from props, use that first for the current user
    if (userData && user.uid === currentUser?.uid) {
      return `${userData.firstName} ${userData.lastName || ''}`.trim();
    }
    
    // If we have a display name, use it
    if (user.displayName) {
      return user.displayName;
    }
    
    // Fallback to email if no display name
    if (user.email) {
      return user.email;
    }
    
    return "User";
  };

  // Update handleSendMessage to correctly set the sender type
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !user || !reportId) return;
    
    try {
      await addDoc(collection(portalDb, 'reportMessages'), {
        reportId,
        senderId: user.uid,
        senderName: getUserDisplayName(user),
        senderType: 'portal', // Changed from 'client' to 'portal' to distinguish from actual clients
        message: newMessage.trim(),
        timestamp: new Date(),
        read: false
      });
      
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  // Function to generate PDF
  const generatePDF = async () => {
    if (!report) return null;
    
    try {
      setIsGeneratingPdf(true);
      // Fetch the template PDF
      const existingPdfBytes = await fetch('/template2.pdf').then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes, {
        ignoreEncryption: true,
        updateMetadata: false
      });
      const form = pdfDoc.getForm();

      // Helper function to format date
      const formatDate = (dateValue) => {
        if (!dateValue) return 'Date: N/A';
        try {
          let date;
          
          // Handle Firebase Timestamp objects that might be serialized in different ways
          if (typeof dateValue === 'object' && dateValue.seconds && dateValue.nanoseconds) {
            // This is a Firebase Timestamp object
            date = new Date(dateValue.seconds * 1000);
          } else if (typeof dateValue === 'string' && dateValue.includes('at')) {
            // This handles the case when it's a string like "March 4, 2025 at 1:37:30 AM UTC-5"
            date = new Date(dateValue);
          } else {
            // Handle normal date strings
            date = new Date(dateValue);
          }
          
          // Check if date is invalid
          if (isNaN(date.getTime())) {
            console.warn('Invalid date encountered:', dateValue);
            return 'Date: N/A';
          }
          
          const monthNames = ["January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
          ];
          return `Date: ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
        } catch (error) {
          console.error('Error formatting date:', error, dateValue);
          return 'Date: N/A';
        }
      };

      const formatShortDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
          let date;
          
          // Handle Firebase Timestamp objects that might be serialized in different ways
          if (typeof dateValue === 'object' && dateValue.seconds && dateValue.nanoseconds) {
            // This is a Firebase Timestamp object
            date = new Date(dateValue.seconds * 1000);
          } else if (typeof dateValue === 'string' && dateValue.includes('at')) {
            // This handles the case when it's a string like "March 4, 2025 at 1:37:30 AM UTC-5"
            date = new Date(dateValue);
          } else {
            // Handle normal date strings
            date = new Date(dateValue);
          }
          
          // Check if date is invalid
          if (isNaN(date.getTime())) {
            console.warn('Invalid date encountered:', dateValue);
            return 'N/A';
          }
          
          return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          });
        } catch (error) {
          console.error('Error formatting short date:', error);
          return 'N/A';
        }
      };
      
      // Helper function to remove USA from address
      const removeUSA = (addr) => {
        if (!addr) return '';
        return addr
          .replace(/,\s*USA$/i, '')
          .replace(/,\s*[A-Z]{2}\s*$/, '')
          .replace(/,\s*United States$/i, '')
          .replace(/,\s*New Jersey$/i, '')
          .replace(/,\s*NJ$/i, '');
      };

      // Fill in form fields using the report data with null checks
      form.getTextField('headerName').setText(`Name: ${report.clientName || 'N/A'}`);
      form.getTextField('headerProperty').setText(`Subject Property: ${report.propertyAddress ? removeUSA(report.propertyAddress) : 'N/A'}`);
      form.getTextField('headerDate').setText(formatDate(report.dateOfInspection));
      form.getTextField('date').setText(formatShortDate(report.dateOfInspection));

      // Generate the complete report text
      let reportText = report.fullDocumentText || '';
      
      // If no full document text, generate based on scenario
      if (!reportText) {
        reportText = 'Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property';

        if (report.scenario === 'noTank') {
          reportText += ` to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). We did not detect any anomalies with our metal detection equipment that match the size of a UST.`;
        } else if (report.scenario === 'anomaly') {
          const location = report.anomalyLocation === 'custom' ? report.customLocation : report.anomalyLocation;
          reportText += ` to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). A metallic anomaly, similar in size to an underground storage tank (UST), was detected${location ? " " + location : ""}.`;
        } else if (report.scenario === 'ustFound') {
          const location = report.anomalyLocation === 'custom' ? report.customLocation : report.anomalyLocation;
          reportText += ` to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). An underground storage tank (UST) was identified${location ? " " + location : ""}.`;
        }

        // Add custom text if present
        if (report.customText) {
          reportText += " " + report.customText.trim();
          if (!report.customText.trim().endsWith('.')) {
            reportText += ".";
          }
        }

        // Add scenario-specific conclusion
        if (report.scenario === 'noTank') {
          reportText += " This does not guarantee the absence of USTs on the property; but it is unlikely.";
        } else if (report.scenario === 'anomaly') {
          reportText += " We recommend requesting OPRA records for any information on previously decommissioned oil tanks. If no records are found, proceed with an intrusive investigation.";
        } else if (report.scenario === 'ustFound') {
          reportText += " This UST may pose a risk of contamination if not addressed. We strongly recommend the removal or proper decommissioning of this UST, along with soil testing for potential contamination.";
        }

        // Add standard closing
        reportText += `\n\nUtilizing electronic metal detection equipment (Fisher TW-6), and a magnetic locating device (Schonstedt 92XTD), the grounds were swept for any buried metallic objects representing a UST. The detection equipment used can detect metallic objects up to a depth of six feet. The property was swept to a distance of at least 30ft around the perimeter of the house.

No other metal anomalies consistent with the size of an underground storage tank were identified during the oil tank sweep.

If you have any questions about this report please feel free to contact us.

Terms and Conditions: This report excludes areas where access to the magnetic locator was restricted due to obstructions such as dense vegetation, denied access, or structural interference. The methods used at this site have been used successfully at many sites to locate underground storage tanks and drums. There can be no guarantee, however, that every target will be detected at a particular site. Subsurface conditions (e.g. rebar/wire mesh under concrete, foundations, structures and others) may prevent some or all geophysical locating methods from working properly.`;
      }

      // Add a note about images if the report has images
      if (report.imageUrls?.length > 0) {
        reportText += `\n\nNOTE: This report contains ${report.imageUrls.length} site photo(s) which can be viewed in the online version at: https://oiltank.app/share-report/${reportId}`;
      }

      form.getTextField('report').setText(reportText);

      // Flatten the form to make the content non-editable
      form.flatten();

      // Now that CORS is fixed, we'll embed the images directly
      if (report.imageUrls?.length > 0) {
        try {
          for (let i = 0; i < report.imageUrls.length; i += 2) {
            const page = pdfDoc.addPage();
            const { width, height } = page.getSize();
            const imageMargin = 50;
            const verticalSpacing = 20;
            const maxImageHeight = (height - (imageMargin * 2) - verticalSpacing) / 2;

            try {
              // Process first image
              const image1Response = await fetch(report.imageUrls[i].url);
              if (!image1Response.ok) {
                console.error(`Failed to fetch image ${i}: ${image1Response.status}`);
                continue; // Skip this page and try the next pair
              }
              
              const image1ArrayBuffer = await image1Response.arrayBuffer();
              const image1Format = report.imageUrls[i].url.toLowerCase().endsWith('.png') ? 'png' : 'jpeg';
              const embeddedImage1 = image1Format === 'png' 
                ? await pdfDoc.embedPng(image1ArrayBuffer)
                : await pdfDoc.embedJpg(image1ArrayBuffer);

              // Calculate dimensions for first image
              const img1Dims = {
                width: 0,
                height: 0
              };
              
              // Calculate the ratio to fit the image within the available space
              const ratio = Math.min(
                (width - (imageMargin * 2)) / embeddedImage1.width,
                maxImageHeight / embeddedImage1.height
              );
              
              img1Dims.width = embeddedImage1.width * ratio;
              img1Dims.height = embeddedImage1.height * ratio;

              // Draw first image
              page.drawImage(embeddedImage1, {
                x: (width - img1Dims.width) / 2,
                y: height - imageMargin - img1Dims.height,
                width: img1Dims.width,
                height: img1Dims.height
              });

              // If there's a second image
              if (i + 1 < report.imageUrls.length) {
                try {
                  const image2Response = await fetch(report.imageUrls[i + 1].url);
                  if (!image2Response.ok) {
                    console.error(`Failed to fetch image ${i+1}: ${image2Response.status}`);
                    continue; // Skip just the second image
                  }
                  
                  const image2ArrayBuffer = await image2Response.arrayBuffer();
                  const image2Format = report.imageUrls[i + 1].url.toLowerCase().endsWith('.png') ? 'png' : 'jpeg';
                  const embeddedImage2 = image2Format === 'png'
                    ? await pdfDoc.embedPng(image2ArrayBuffer)
                    : await pdfDoc.embedJpg(image2ArrayBuffer);

                  // Calculate dimensions for second image
                  const img2Dims = {
                    width: 0,
                    height: 0
                  };
                  
                  // Calculate the ratio to fit the image within the available space
                  const ratio = Math.min(
                    (width - (imageMargin * 2)) / embeddedImage2.width,
                    maxImageHeight / embeddedImage2.height
                  );
                  
                  img2Dims.width = embeddedImage2.width * ratio;
                  img2Dims.height = embeddedImage2.height * ratio;

                  // Draw second image
                  page.drawImage(embeddedImage2, {
                    x: (width - img2Dims.width) / 2,
                    y: imageMargin,
                    width: img2Dims.width,
                    height: img2Dims.height
                  });
                } catch (error) {
                  console.error(`Error processing second image (${i+1}):`, error);
                  // Continue with the PDF without this image
                }
              }
            } catch (error) {
              console.error(`Error processing image ${i}:`, error);
              // Continue with the next page
            }
          }
        } catch (error) {
          console.error('Error processing images:', error);
          toast.error('Error embedding images');
          
          // Fallback: If image embedding fails, add a page with a note
          const page = pdfDoc.addPage();
          const { width, height } = page.getSize();
          
          // Draw text explaining where to view images
          page.drawText('Site Photos', {
            x: 50,
            y: height - 50,
            size: 18,
            color: rgb(0, 0, 0)
          });
          
          page.drawText(`This report includes ${report.imageUrls.length} site photos which are not included in this PDF`, {
            x: 50,
            y: height - 80,
            size: 12,
            color: rgb(0, 0, 0)
          });
          
          page.drawText('To view all site photos, please visit:', {
            x: 50,
            y: height - 110,
            size: 12,
            color: rgb(0, 0, 0)
          });
          
          page.drawText(`https://oiltank.app/share-report/${reportId}`, {
            x: 50,
            y: height - 140,
            size: 12,
            color: rgb(0, 0, 1)
          });
        }
      }

      // Save the PDF and convert it to a blob URL
      const pdfBytes = await pdfDoc.save({
        useObjectStreams: false,
        addDefaultPage: false,
        updateMetadata: false
      });
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setIsGeneratingPdf(false);
      return url;
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
      setIsGeneratingPdf(false);
      return null;
    }
  };

  // Function to handle download
  const handleDownload = async () => {
    try {
      toast.loading('Preparing download...');
      
      // Generate PDF if not already generated
      let url = pdfUrl;
      if (!url) {
        url = await generatePDF();
        if (!url) {
          toast.dismiss();
          toast.error('Failed to generate PDF');
          return;
        }
      }
      
      // Track the download
      try {
        if (reportId) {
          const reportRef = doc(sweepDb, 'inspections', reportId);
          
          // Create a download entry with timestamp
          const newDownloadEntry = {
            timestamp: new Date().toISOString(),
            userId: user?.uid || 'anonymous',
            platform: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            source: 'portal'
          };
          
          // Use a transaction to update both count and log
          await runTransaction(sweepDb, async (transaction) => {
            const reportDoc = await transaction.get(reportRef);
            if (!reportDoc.exists()) {
              throw new Error("Document does not exist!");
            }
            
            const currentDownloadCount = reportDoc.data().downloadCount || 0;
            const currentDownloadLog = reportDoc.data().downloadLog || [];
            
            transaction.update(reportRef, {
              downloadCount: currentDownloadCount + 1,
              downloadLog: [...currentDownloadLog, newDownloadEntry]
            });
          });
        }
      } catch (error) {
        console.error('Error tracking download:', error);
        // Continue with download even if tracking fails
      }
      
      // Dismiss loading toast
      toast.dismiss();
      toast.success('Download started');
      
      // Start the download
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const propertyName = report.propertyAddress ? report.propertyAddress.split(',')[0] : 'Property';
      const fileName = `Oil Tank Sweep - ${report.clientName || 'Client'} - ${propertyName}.pdf`;
      
      if (isMobile) {
        // For mobile devices, open in a new tab
        setTimeout(() => {
          window.open(url, '_blank');
        }, 300);
      } else {
        // For desktop browsers, use download attribute
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.dismiss();
      toast.error('Error downloading report');
    }
  };

  // Clean up the PDF URL when component unmounts
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold">Loading Report...</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 p-8 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-500">Loading report data...</p>
            <button 
              onClick={onClose}
              className="mt-6 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold">Report Not Found</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 p-8">
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-red-700">
                This report could not be found. It may have been deleted or you may not have permission to view it.
              </p>
            </div>
            <div className="mt-8 flex justify-center">
              <button 
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const scenarioInfo = getScenarioInfo(report.scenario);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto py-8">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 truncate max-w-[80%]">
            {formatAddress(report.propertyAddress || 'Property Address')}
            {report.clientName ? ` - ${report.clientName}` : ''}
          </h2>
          <div className="flex items-center space-x-3">
            <a
              href={`https://oiltank.app/share-report/${reportId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 flex items-center"
              title="Open in oiltank.app"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Status Banner */}
        <div className={`px-4 py-2 ${scenarioInfo.bgColor} ${scenarioInfo.borderColor} border-b flex items-center`}>
          <div className="flex items-center">
            {scenarioInfo.icon}
            <span className={`ml-2 font-medium ${scenarioInfo.color}`}>
              {scenarioInfo.label}
            </span>
          </div>
        </div>

        {/* Basic Info */}
        <div className="border-b px-6 py-4 flex flex-wrap gap-6">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-gray-500" />
            <span className="text-sm">
              <span className="text-gray-500">Inspection Date:</span>{' '}
              <span className="font-medium">{formatDate(report.dateOfInspection)}</span>
            </span>
          </div>
          <div className="flex items-center">
            <MapPin className="w-4 h-4 mr-2 text-gray-500" />
            <span className="text-sm">
              <span className="text-gray-500">Property:</span>{' '}
              <span className="font-medium">{formatAddress(report.propertyAddress)}</span>
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === 'details' 
                  ? 'border-b-2 border-blue-500 text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('details')}
            >
              Report Details
            </button>
            {report?.imageUrls && Array.isArray(report.imageUrls) && report.imageUrls.length > 0 && (
              <button
                className={`px-4 py-3 text-sm font-medium ${
                  activeTab === 'photos' 
                    ? 'border-b-2 border-blue-500 text-blue-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('photos')}
              >
                Site Photos ({report.imageUrls.length})
              </button>
            )}
            <button
              className={`px-4 py-3 text-sm font-medium flex items-center ${
                activeTab === 'messages' 
                  ? 'border-b-2 border-blue-500 text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => {
                setActiveTab('messages');
                // Mark messages as read when clicking on the messages tab
                if (messages.length > 0) {
                  // Mark messages as read in Firestore
                  markMessagesAsRead(messages);
                  
                  // Also update local state immediately for faster UI response
                  setMessages(prev => 
                    prev.map(msg => 
                      msg.senderId !== user.uid && !msg.read 
                        ? {...msg, read: true} 
                        : msg
                    )
                  );
                }
              }}
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Messages
              {activeTab !== 'messages' && messages.filter(m => m.senderId !== user?.uid && !m.read).length > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {messages.filter(m => m.senderId !== user?.uid && !m.read).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'details' && (
            <div className="prose max-w-none">
              {renderReportContent()}
            </div>
          )}
          
          {activeTab === 'photos' && (
            <div>
              {report.imageUrls && report.imageUrls.length > 0 ? (
                <div>
                  {/* Main image viewer */}
                  <div className="mb-4 rounded-lg overflow-hidden border border-gray-200 cursor-zoom-in">
                    <img 
                      src={report.imageUrls[activeImageIndex].url} 
                      alt={`Site photo ${activeImageIndex + 1}`}
                      className="w-full object-contain max-h-96"
                      onClick={handleImageClick}
                    />
                  </div>

                  {/* Thumbnails */}
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {report.imageUrls.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImageIndex(index)}
                        className={`border rounded overflow-hidden hover:opacity-75 transition-opacity ${
                          index === activeImageIndex ? 'ring-2 ring-blue-500' : ''
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-16 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No photos available for this report.
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'messages' && (
            <div className="flex flex-col h-full">
              {isLoadingMessages ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto mb-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        No messages yet. Start a conversation about this report.
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div 
                          key={msg.id} 
                          className={`p-3 rounded-lg max-w-[80%] ${
                            msg.senderId !== user?.uid
                              ? 'ml-auto bg-gray-100 text-gray-800' 
                              : 'bg-blue-500 text-white'
                          }`}
                        >
                          <div className="text-xs opacity-70 mb-1 flex justify-between">
                            <span>
                              {/* Show the actual sender name */}
                              {msg.senderName}
                              {' '}
                              <span className={`text-xs ${msg.senderId !== user?.uid ? 'opacity-70' : 'opacity-80'}`}>
                                ({msg.senderType === 'inspector' ? 'Inspector' : 
                                  msg.senderType === 'client' ? 'Client' : 'Portal User'}) • {new Date(msg.timestamp.toDate()).toLocaleString()}
                              </span>
                            </span>
                            {msg.senderId === user?.uid && (
                              <button 
                                onClick={() => handleDeleteMessage(msg.id)}
                                className={`ml-2 ${msg.senderId === user?.uid ? 'text-white hover:text-red-200' : 'text-gray-500 hover:text-red-500'}`}
                                title="Delete message"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <div>{msg.message}</div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2 mt-4">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="p-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            Report ID: {reportId}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownload}
              disabled={isGeneratingPdf}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              {isGeneratingPdf ? 'Preparing...' : 'Download PDF'}
            </button>
            <a
              href={`https://oiltank.app/share-report/${reportId}?source=portal`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Full Report
            </a>
          </div>
        </div>
      </div>

      {/* Image Zoom Lightbox */}
      {showImageZoom && report?.imageUrls && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center overflow-hidden"
          tabIndex={0} // Make the container focusable for keyboard events
        >
          <div className="absolute top-4 right-4 flex gap-2">
            <button 
              onClick={handleZoomIn} 
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
              title="Zoom in"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-6 h-6 text-white" />
            </button>
            <button 
              onClick={handleZoomOut} 
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
              title="Zoom out"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-6 h-6 text-white" />
            </button>
            <button 
              onClick={handleCloseZoom} 
              className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
              title="Close"
              aria-label="Close zoomed view"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          
          {/* Navigation buttons for previous/next image */}
          {report.imageUrls.length > 1 && (
            <div className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between px-4">
              <button
                onClick={() => activeImageIndex > 0 && setActiveImageIndex(prev => prev - 1)}
                className={`p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors ${
                  activeImageIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={activeImageIndex === 0}
                aria-label="Previous image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => activeImageIndex < report.imageUrls.length - 1 && setActiveImageIndex(prev => prev + 1)}
                className={`p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors ${
                  activeImageIndex === report.imageUrls.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={activeImageIndex === report.imageUrls.length - 1}
                aria-label="Next image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
          
          {zoomLevel > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-white/20 rounded-full text-white text-sm flex items-center">
              <Move className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Drag to pan image</span>
              <span className="sm:hidden">Pinch to zoom, drag to pan</span>
            </div>
          )}
          
          <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/20 rounded-full text-white text-sm">
            {activeImageIndex + 1} / {report.imageUrls.length}
          </div>
          
          <div 
            className="w-full h-full flex items-center justify-center"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
          >
            <img 
              ref={imageRef}
              src={report.imageUrls[activeImageIndex].url} 
              alt={`Site photo ${activeImageIndex + 1} (zoomed)`}
              className="max-w-full max-h-full object-contain transition-transform duration-100"
              style={{ 
                transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportModal; 